/**
 * auth.service.ts — D.1.2.3 Firebase Auth Service Wrapper
 *
 * Anti-corruption layer around Firebase Authentication.
 *
 * Design principles:
 *   1. The rest of the app imports ONLY from this service — never from
 *      'firebase/auth' directly. This limits the blast radius if Firebase's
 *      API changes.
 *   2. The service is a class (not a module-level singleton) so tests can
 *      instantiate fresh copies without module-level state bleed.
 *   3. Error handling: Firebase errors are re-thrown unchanged for now.
 *      Phase 2 will map Firebase error codes → user-friendly messages.
 *
 * Usage:
 *   import { authService } from '@/services/auth.service';
 *   const user = await authService.signIn(email, password);
 */
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from './firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal user shape exposed by the auth service — decoupled from Firebase types. */
export interface AuthUser {
  readonly uid: string;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly emailVerified: boolean;
  /** Returns the Firebase ID token (JWT). Pass true to force a refresh. */
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

/** Unsubscribe function returned by onAuthStateChanged. */
export type Unsubscribe = () => void;

// ---------------------------------------------------------------------------
// AuthService class
// ---------------------------------------------------------------------------

export class AuthService {
  /**
   * Internal reference to the current Firebase User.
   * Kept in sync by a private onAuthStateChanged listener started in the
   * constructor.
   */
  private currentUser: User | null = null;

  constructor() {
    // Keep currentUser in sync so getToken() can read it synchronously.
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
    });
  }

  // ---- Sign-up -----------------------------------------------------------

  /**
   * Create a new user account with email and password.
   *
   * @throws Firebase AuthError if the email is already in use, the password
   *   is too weak, etc.
   */
  async signUp(email: string, password: string): Promise<AuthUser> {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    // Send verification email but don't block — fire and forget
    void sendEmailVerification(credential.user).catch((err: unknown) => {
      console.warn('[AuthService] sendEmailVerification failed:', err);
    });
    return credential.user;
  }

  // ---- Sign-in -----------------------------------------------------------

  /**
   * Sign in an existing user with email and password.
   *
   * @throws Firebase AuthError on wrong credentials, user not found, etc.
   */
  async signIn(email: string, password: string): Promise<AuthUser> {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential.user;
  }

  // ---- Sign-out ----------------------------------------------------------

  /**
   * Sign out the current user and clear local auth state.
   *
   * @throws Firebase AuthError on network failure.
   */
  async signOut(): Promise<void> {
    await signOut(auth);
  }

  // ---- Auth state observer -----------------------------------------------

  /**
   * Register a callback that fires whenever the auth state changes (login /
   * logout / token refresh).
   *
   * @returns Unsubscribe function — call it to stop listening.
   */
  onAuthStateChanged(callback: (user: User | null) => void): Unsubscribe {
    return onAuthStateChanged(auth, callback);
  }

  // ---- Token retrieval ---------------------------------------------------

  /**
   * Returns the current user's Firebase ID token, or null if not logged in.
   *
   * @param forceRefresh Pass true to force a token refresh even if the
   *   current token has not expired. Used by the API client's 401 interceptor.
   */
  async getToken(forceRefresh = false): Promise<string | null> {
    if (this.currentUser === null) {
      return null;
    }
    return this.currentUser.getIdToken(forceRefresh);
  }

  // ---- Password reset ----------------------------------------------------

  /**
   * Send a password reset email to the given address.
   *
   * @throws Firebase AuthError if the email is not registered.
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  }
}

/**
 * Singleton instance — import this in application code.
 *
 * Tests create their own instances via `new AuthService()` to avoid
 * singleton state bleed.
 */
export const authService = new AuthService();
